<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LabelsAPI from 'dashboard/api/labels';
import FunnelsAPI from 'dashboard/api/funnels';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ label: { type: Object, required: true } });
const { t } = useI18n();
const rules = ref([]);
const labels = ref([]);
const funnels = ref([]);
const error = ref('');
const busy = ref(false);
const editing = ref(null);
const emptyRule = () => ({
  name: '',
  trigger_event: 'added',
  trigger_scope: props.label.crm_scope || 'contact',
  action_name: 'remove_label',
  target_label_id: null,
  funnel_id: null,
  stage_id: '',
  active: false,
});
const draft = ref(emptyRule());
const crmAction = computed(() =>
  ['start_crm', 'advance_crm', 'handoff'].includes(draft.value.action_name)
);
const selectedFunnel = computed(() =>
  funnels.value.find(item => item.id === draft.value.funnel_id)
);
const stages = computed(() => selectedFunnel.value?.columns || []);
const load = async () => {
  error.value = '';
  try {
    const [ruleResponse, labelResponse, funnelResponse] = await Promise.all([
      LabelsAPI.automationRules(props.label.id),
      LabelsAPI.get(),
      FunnelsAPI.get(),
    ]);
    rules.value = ruleResponse.data;
    labels.value = labelResponse.data.payload;
    funnels.value = funnelResponse.data;
  } catch {
    error.value = t('LABEL_MGMT.RULES.LOAD_ERROR');
  }
};
watch(
  () => props.label.id,
  () => {
    draft.value = emptyRule();
    editing.value = null;
    load();
  },
  { immediate: true }
);
const edit = rule => {
  editing.value = rule.id;
  draft.value = { ...rule };
};
const reset = () => {
  editing.value = null;
  draft.value = emptyRule();
};
const save = async () => {
  busy.value = true;
  error.value = '';
  try {
    const data = { ...draft.value };
    if (crmAction.value) data.target_label_id = null;
    else {
      data.funnel_id = null;
      data.stage_id = null;
    }
    if (editing.value)
      await LabelsAPI.updateAutomationRule(props.label.id, editing.value, data);
    else await LabelsAPI.createAutomationRule(props.label.id, data);
    await load();
    reset();
  } catch (e) {
    error.value = e.response?.data?.error || t('LABEL_MGMT.RULES.SAVE_ERROR');
  } finally {
    busy.value = false;
  }
};
const toggle = async rule => {
  busy.value = true;
  try {
    await LabelsAPI.updateAutomationRule(props.label.id, rule.id, {
      active: !rule.active,
    });
    await load();
  } catch {
    error.value = t('LABEL_MGMT.RULES.SAVE_ERROR');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div
    class="flex max-h-[80vh] min-w-0 flex-col gap-4 overflow-y-auto p-5 text-sm text-n-slate-12"
  >
    <h2 class="m-0 text-lg font-semibold">
      {{ $t('LABEL_MGMT.RULES.TITLE') }} · {{ label.title }}
    </h2>
    <p class="m-0 text-xs text-n-slate-11">{{ $t('LABEL_MGMT.RULES.HELP') }}</p>
    <p v-if="error" role="alert" class="m-0 text-n-ruby-11">{{ error }}</p>
    <ul class="m-0 flex list-none flex-col gap-2 p-0">
      <li
        v-for="rule in rules"
        :key="rule.id"
        class="flex min-w-0 flex-wrap items-center gap-2 rounded border border-n-weak p-2"
      >
        <span class="min-w-0 flex-1 break-words"
          >{{ rule.name }} ·
          {{
            rule.active
              ? $t('LABEL_MGMT.RULES.ACTIVE')
              : $t('LABEL_MGMT.RULES.PAUSED')
          }}<!-- prettier-ignore -->
          <small
            v-if="rule.last_execution"
            class="block text-n-slate-11"
            >{{ $t('LABEL_MGMT.RULES.LAST_RESULT') }}:
            {{ rule.last_execution.status }}</small></span
        >
        <Button
          type="button"
          size="xs"
          variant="ghost"
          icon="i-lucide-pen"
          :aria-label="$t('LABEL_MGMT.RULES.EDIT')"
          @click="edit(rule)"
        />
        <Button
          type="button"
          size="xs"
          variant="ghost"
          :icon="rule.active ? 'i-lucide-pause' : 'i-lucide-play'"
          :aria-label="$t('LABEL_MGMT.RULES.TOGGLE')"
          :disabled="busy"
          @click="toggle(rule)"
        />
      </li>
    </ul>
    <form
      class="grid min-w-0 gap-3 border-t border-n-weak pt-3 sm:grid-cols-2"
      @submit.prevent="save"
    >
      <label class="flex min-w-0 flex-col gap-1 sm:col-span-2"
        >{{ $t('LABEL_MGMT.RULES.NAME')
        }}<input
          v-model.trim="draft.name"
          required
          class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
      /></label>
      <label class="flex min-w-0 flex-col gap-1"
        >{{ $t('LABEL_MGMT.RULES.EVENT')
        }}<select
          v-model="draft.trigger_event"
          class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
        >
          <option value="added">{{ $t('LABEL_MGMT.RULES.ADDED') }}</option>
          <option value="removed">{{ $t('LABEL_MGMT.RULES.REMOVED') }}</option>
        </select></label
      >
      <label class="flex min-w-0 flex-col gap-1"
        >{{ $t('LABEL_MGMT.CRM_SCOPE')
        }}<select
          v-model="draft.trigger_scope"
          class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
        >
          <option value="contact">{{ $t('LABEL_MGMT.SCOPES.contact') }}</option>
          <option value="conversation">
            {{ $t('LABEL_MGMT.SCOPES.conversation') }}
          </option>
        </select></label
      >
      <label class="flex min-w-0 flex-col gap-1 sm:col-span-2"
        >{{ $t('LABEL_MGMT.RULES.ACTION')
        }}<select
          v-model="draft.action_name"
          class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
        >
          <option value="add_label">
            {{ $t('LABEL_MGMT.RULES.ADD_LABEL') }}
          </option>
          <option value="remove_label">
            {{ $t('LABEL_MGMT.RULES.REMOVE_LABEL') }}
          </option>
          <option value="start_crm">
            {{ $t('LABEL_MGMT.RULES.START_CRM') }}
          </option>
          <option value="advance_crm">
            {{ $t('LABEL_MGMT.RULES.ADVANCE_CRM') }}
          </option>
          <option value="handoff">{{ $t('LABEL_MGMT.RULES.HANDOFF') }}</option>
        </select></label
      >
      <!-- prettier-ignore -->
      <label
        v-if="!crmAction"
        class="flex min-w-0 flex-col gap-1 sm:col-span-2"
        >{{ $t('LABEL_MGMT.RULES.TARGET_LABEL')
        }}<select
          v-model.number="draft.target_label_id"
          required
          class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
        >
          <option :value="null">—</option>
          <option v-for="item in labels" :key="item.id" :value="item.id">
            {{ item.title }}
          </option>
        </select></label>
      <template v-else>
        <label class="flex min-w-0 flex-col gap-1"
          >{{ $t('LABEL_MGMT.RULES.FUNNEL')
          }}<select
            v-model.number="draft.funnel_id"
            required
            class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
            @change="draft.stage_id = ''"
          >
            <option :value="null">—</option>
            <option v-for="item in funnels" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select></label
        >
        <label class="flex min-w-0 flex-col gap-1"
          >{{ $t('LABEL_MGMT.RULES.STAGE')
          }}<select
            v-model="draft.stage_id"
            required
            class="min-w-0 rounded border border-n-weak bg-n-solid-2 p-2"
          >
            <option value="">—</option>
            <option v-for="item in stages" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select></label
        >
      </template>
      <label class="flex items-center gap-2 sm:col-span-2"
        ><input v-model="draft.active" type="checkbox" />{{
          $t('LABEL_MGMT.RULES.ACTIVATE')
        }}</label
      >
      <div class="flex gap-2 sm:col-span-2">
        <Button
          type="submit"
          :label="$t('LABEL_MGMT.RULES.SAVE')"
          :disabled="busy"
        /><Button
          v-if="editing"
          type="button"
          variant="ghost"
          :label="$t('LABEL_MGMT.RULES.CANCEL')"
          @click="reset"
        />
      </div>
    </form>
  </div>
</template>
